import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TableSortLabel,
  TextField, InputAdornment, Chip, IconButton, CircularProgress,
  Snackbar, Alert, Tooltip, Divider, Stack, Badge, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { styled, alpha, keyframes } from '@mui/material/styles';

import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from './authUtils';
import { backend_api_base_url } from './config/urlconfig';

// ─── Animations ──────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: none; }
`;

// ─── Styled ───────────────────────────────────────────────────────────────────
const PageShell = styled(Box)({
  background: '#f8f9fc',
  minHeight: '100vh',
  padding: 24,
});

const StatCard = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2.5),
  boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
  animation: `${fadeUp} .35s ease both`,
  display: 'flex', alignItems: 'center', gap: 16,
}));

// ─── Inline edit cell ─────────────────────────────────────────────────────────
const EditableCell = ({ value, onSave, placeholder }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  const commit = () => { onSave(draft.trim()); setEditing(false); };
  const cancel = () => { setDraft(value || ''); setEditing(false); };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          size="small"
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
          placeholder={placeholder}
          sx={{ width: 130, '& input': { fontSize: '0.82rem', py: 0.6 } }}
        />
        <IconButton size="small" onClick={commit} sx={{ color: '#15803d' }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
        <IconButton size="small" onClick={cancel} sx={{ color: '#dc2626' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover .edit-icon': { opacity: 1 } }}
      onClick={() => setEditing(true)}>
      {value ? (
        <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{value}</Typography>
      ) : (
        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#f59e0b', fontStyle: 'italic' }}>
          Not set
        </Typography>
      )}
      <EditIcon className="edit-icon" sx={{ fontSize: 14, color: '#94a3b8', opacity: 0, transition: 'opacity .15s', ml: 0.3 }} />
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('displayName');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(25);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ displayName: '', email: '', city: '', state: '', department: '', jobTitle: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  // ── Load users on mount ───────────────────────────────────────────────────
  useEffect(() => {
    loadUsers();
  }, []);

  const sendAuthenticatedRequest = async (url, method, body) => {
    const token = await getApiToken(instance, account);
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.headers.get('content-type')?.includes('application/json') ? res.json() : res.text();
  };


  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await sendAuthenticatedRequest(`${backend_api_base_url}/api/v2/oms-users`, 'GET');
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch from Azure AD ───────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await sendAuthenticatedRequest(`${backend_api_base_url}/api/v2/oms-users/sync`, 'POST');
      showToast(
        `Sync complete — ${result.inserted} added, ${result.updated} updated, ${result.deactivated} deactivated`,
        'success'
      );
      await loadUsers(); 
    } catch {
      showToast('Sync failed — check server logs', 'error');
    } finally {
      setSyncing(false);
    }
  };

  // ── Inline save ───────────────────────────────────────────────────────────
  const handleLocationSave = async (userId, field, value) => {
    setSavingId(userId);
    try {
      const patch = { [field]: value };
      const updated = await sendAuthenticatedRequest(`${backend_api_base_url}/api/v2/oms-users/${userId}/location`, 'PATCH', patch);

      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updated } : u));
      showToast('Location updated - Regional tab will reflect this on next load', 'success');
    } catch {
      showToast('Failed to save - please try again', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async () => {
    if (!createForm.displayName.trim()) {
      setCreateError('Display name is required.');
      return;
    }
    if (!createForm.email.trim()) {
      setCreateError('Email is required.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createForm.email.trim())) {
      setCreateError('Please enter a valid email address (e.g., user@example.com).');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const created = await sendAuthenticatedRequest(
        `${backend_api_base_url}/api/v2/oms-users/create`, 'POST', createForm
      );
      setUsers(prev => [...prev, created]);
      setCreateOpen(false);
      setCreateForm({ displayName: '', email: '', city: '', state: '', department: '', jobTitle: '' });
      showToast(`User "${created.displayName}" created successfully`, 'success');
    } catch (e) {
      if (e.message.includes('409') || e.message.includes('already exists')) {
        setCreateError('A user with this email already exists.');
      } else if (e.message.includes('validation') || e.message.includes('invalid')) {
        setCreateError(e.message);
      } else {
        setCreateError('Failed to create user');
      }
    } finally {
      setCreating(false);
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.isActive).length,
    inactive: users.filter(u => !u.isActive).length,
    unresolved: users.filter(u => !u.city || !u.state).length,
  }), [users]);

  // ── Filter + sort + search ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let rows = [...users];

    if (filter === 'unresolved') rows = rows.filter(u => !u.city || !u.state);
    if (filter === 'inactive') rows = rows.filter(u => !u.isActive);

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(u =>
        (u.displayName || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.city || '').toLowerCase().includes(q) ||
        (u.state || '').toLowerCase().includes(q) ||
        (u.department || '').toLowerCase().includes(q)
      );
    }

    rows.sort((a, b) => {
      let va =a[orderBy];
      let vb =b[orderBy];
      if (typeof va === 'boolean' && typeof vb === 'boolean') {
        return order === 'asc'
          ? (va === vb ? 0 : va ? 1 : -1)
          : (va === vb ? 0 : va ? -1 : 1);
      }
      // handle string and null fields
        va = (va || '').toString().toLowerCase();
        vb = (vb || '').toString().toLowerCase();

      // const va = (a[orderBy] || '').toString().toLowerCase();
      // const vb = (b[orderBy] || '').toString().toLowerCase();
      return order === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return rows;
  }, [users, filter, search, order, orderBy]);

  const paginated = filtered.slice(page * rpp, page * rpp + rpp);

  const handleSort = col => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(col);
    setPage(0);
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const exportXlsx = () => {
    const rows = filtered.map(u => ({
      'Display Name': u.displayName,
      'Email': u.email || '',
      'City': u.city || '',
      'State': u.state || '',
      'Department': u.department || '',
      'Job Title': u.jobTitle || '',
      'Status': u.isActive ? 'Active' : 'Inactive',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0] || {}).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'oms_users.xlsx');
  };

  const showToast = (msg, severity = 'info') => setToast({ msg, severity });

  // ── Table header style ────────────────────────────────────────────────────
  const th = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.79rem', whiteSpace: 'nowrap' };

  return (
    <PageShell>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={900} letterSpacing="-0.5px">
            Users Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
            Sync from Azure AD · Edit city / state for Unresolved records · Changes reflect immediately in Regional analytics
          </Typography>
        </Box>

        {/* ── FETCH USERS button ── */}
        <Button
          variant="contained"
          size="large"
          startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
          onClick={handleSync}
          disabled={syncing}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 2.5,
            px: 3, py: 1.2, fontSize: '0.95rem',
            background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
            boxShadow: '0 4px 14px rgba(29,78,216,0.35)',
            '&:hover': { background: 'linear-gradient(135deg,#1e40af,#2563eb)' },
          }}
        >
          {syncing ? 'Fetching from Azure AD…' : 'Fetch Users'}
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => setCreateOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2.5, px: 3, py: 1.2, fontSize: '0.95rem' }}
        >
          + Create User
        </Button>
      </Box>

      {/* ── Stat cards ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Users', value: stats.total, icon: <PeopleIcon sx={{ color: '#1d4ed8' }} />, bg: '#eff6ff', filter: 'all' },
          { label: 'Active', value: stats.active, icon: <CheckCircleIcon sx={{ color: '#15803d' }} />, bg: '#f0fdf4', filter: 'all' },
          { label: 'Left Org', value: stats.inactive, icon: <PeopleIcon sx={{ color: '#6b7280' }} />, bg: '#f1f5f9', filter: 'inactive' },
          { label: 'Unresolved', value: stats.unresolved, icon: <WarningAmberIcon sx={{ color: '#d97706' }} />, bg: '#fffbeb', filter: 'unresolved' },
        ].map((s, i) => (
          <StatCard
            key={s.label}
            sx={{
              flex: '1 1 160px', cursor: 'pointer', animationDelay: `${i * 60}ms`,
              border: filter === s.filter && s.filter !== 'all' ? '2px solid #1d4ed8' : '1px solid transparent',
              '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
            }}
            onClick={() => { setFilter(f => f === s.filter ? 'all' : s.filter); setPage(0); }}
          >
            {s.icon}
            <Box>
              <Typography variant="h5" fontWeight={800}>{s.value}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{s.label}</Typography>
            </Box>
          </StatCard>
        ))}
      </Box>

      {/* ── Table paper ── */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

        {/* toolbar */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid #f1f5f9' }}>
          <TextField
            size="small"
            placeholder="Search name, email, city, department…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 320 }}
          />

          {/* Quick-filter chips */}
          <Stack direction="row" spacing={1}>
            {[
              { key: 'all', label: 'All' },
              { key: 'unresolved', label: `Unresolved (${stats.unresolved})` },
              { key: 'inactive', label: `Left Org (${stats.inactive})` },
            ].map(f => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                onClick={() => { setFilter(f.key); setPage(0); }}
                sx={{
                  fontWeight: 700, fontSize: '0.75rem',
                  bgcolor: filter === f.key ? '#1d4ed8' : '#f1f5f9',
                  color: filter === f.key ? '#fff' : '#475569',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: filter === f.key ? '#1e40af' : '#e2e8f0' },
                }}
              />
            ))}
          </Stack>

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {filtered.length} of {users.length} users
            </Typography>
            <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportXlsx}
              sx={{ textTransform: 'none' }}>
              Export
            </Button>
          </Box>
        </Box>

        {/* table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, gap: 2 }}>
            <CircularProgress size={32} />
            <Typography color="text.secondary">Loading users…</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { label: '#', key: null },
                      { label: 'Name', key: 'displayName' },
                      { label: 'Email', key: 'email' },
                      { label: 'City', key: 'city' },
                      { label: 'State', key: 'state' },
                      { label: 'Department', key: 'department' },
                      { label: 'Status', key: 'isActive' },
                    ].map(col => (
                      <TableCell key={col.label} sx={th}>
                        {col.key ? (
                          <TableSortLabel
                            active={orderBy === col.key}
                            direction={orderBy === col.key ? order : 'asc'}
                            onClick={() => handleSort(col.key)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                        No users match the current filter.
                      </TableCell>
                    </TableRow>
                  ) : paginated.map((u, i) => {
                    const isSaving = savingId === u.id;
                    const isUnresolved = !u.city || !u.state;

                    return (
                      <TableRow
                        key={u.id}
                        hover
                        sx={{
                          opacity: isSaving ? 0.6 : 1,
                          transition: 'opacity .2s',
                          // Amber tint for unresolved rows
                          bgcolor: isUnresolved ? alpha('#f59e0b', 0.04) : 'inherit',
                        }}
                      >
                        {/* # */}
                        <TableCell sx={{ color: '#94a3b8', fontSize: '0.78rem', width: 40 }}>
                          {page * rpp + i + 1}
                        </TableCell>

                        {/* Name */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                                background: u.isActive
                                  ? 'linear-gradient(135deg,#1d4ed8,#3b82f6)'
                                  : 'linear-gradient(135deg,#94a3b8,#cbd5e1)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontSize: '0.7rem', fontWeight: 800,
                              }}
                            >
                              {(u.displayName || '?').charAt(0).toUpperCase()}
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.83rem' }}>
                                {u.displayName}
                              </Typography>
                              {!u.isActive && (
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                                  Left organisation
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Email */}
                        <TableCell sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {u.email || '—'}
                        </TableCell>

                        {/* City — inline editable */}
                        <TableCell>
                          {isSaving ? (
                            <CircularProgress size={14} />
                          ) : (
                            <Tooltip title="Click to edit city" placement="top">
                              <span>
                                <EditableCell
                                  value={u.city}
                                  placeholder="Enter city"
                                  onSave={val => handleLocationSave(u.id, 'city', val)}
                                />
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* State — inline editable */}
                        <TableCell>
                          {isSaving ? (
                            <CircularProgress size={14} />
                          ) : (
                            <Tooltip title="Click to edit state" placement="top">
                              <span>
                                <EditableCell
                                  value={u.state}
                                  placeholder="Enter state"
                                  onSave={val => handleLocationSave(u.id, 'state', val)}
                                />
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* Department */}
                        <TableCell sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {u.department || '—'}
                        </TableCell>

                        {/* Status chip */}
                        <TableCell>
                          <Chip
                            size="small"
                            label={u.isActive ? 'Active' : 'Inactive'}
                            sx={{
                              height: 20, fontSize: '0.7rem', fontWeight: 700,
                              bgcolor: u.isActive ? '#f0fdf4' : '#f1f5f9',
                              color: u.isActive ? '#15803d' : '#64748b',
                              border: `1px solid ${u.isActive ? '#bbf7d0' : '#e2e8f0'}`,
                            }}
                          />
                          {isUnresolved && (
                            <Tooltip title="City or state missing — edit inline to fix">
                              <WarningAmberIcon sx={{ fontSize: 14, color: '#f59e0b', ml: 0.5, verticalAlign: 'middle' }} />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rpp}
              onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* ── Info footer ── */}
      <Box sx={{ mt: 2, px: 1 }}>
        <Typography variant="caption" color="text.secondary">
          💡 <strong>City</strong> and <strong>State</strong> columns are editable inline — click any cell to fix.
          Changes save immediately and the Regional analytics tab will reflect them on next page load.
          All other fields (name, email, department) are read-only and sourced from Azure AD.
        </Typography>
      </Box>

      {/* ── Toast ── */}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast(null)} severity={toast?.severity || 'info'}
          sx={{ fontWeight: 600, borderRadius: 2, minWidth: 320 }}>
          {toast?.msg}
        </Alert>
      </Snackbar>

      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setCreateError(''); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Create User Manually</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This creates a user only in the OMS database, not in Azure AD.
            Use this for contractors or anyone not in AD whose cost sheets need a city assigned.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {[
              { key: 'displayName', label: 'Display Name *' },
              { key: 'email', label: 'Email *' },
              { key: 'city', label: 'City' },
              { key: 'state', label: 'State' },
              { key: 'department', label: 'Department' },
              { key: 'jobTitle', label: 'Job Title' },
            ].map(f => (
              <TextField key={f.key} size="small" label={f.label} value={createForm[f.key]}
                onChange={e => setCreateForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
              />
            ))}
            {createError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>{createError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => { setCreateOpen(false); setCreateError(''); }}
            sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
            {creating ? 'Creating…' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default UsersManagement;